/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 *
 * This  software  has  been  provided pursuant  to  a  License
 * Agreement  containing  restrictions on  its  use.   The  software
 * contains  valuable  trade secrets and proprietary information  of
 * Pegasystems Inc and is protected by  federal   copyright law.  It
 * may  not be copied,  modified,  translated or distributed in  any
 * form or medium,  disclosed to third parties or used in any manner
 * not provided for in  said  License Agreement except with  written
 * authorization from Pegasystems Inc.
 */

import type { Filter } from '../datapage/types';
import Utils from '../utils/utils';

import type {
  DataViewCall,
  DataViewCallCombiner,
  DataViewCallComparator,
  DataViewCallSeparator
} from './buffer-utils-types';

const convertFilterToInComparator = (filterConditions: Filter['filterConditions']) => {
  const value = filterConditions?.F0.rhs.value as string;
  filterConditions.F0.rhs = { values: [value] };
  filterConditions.F0.comparator = 'IN';
};

const checkIfBothFilterConditionsHaveASingleFilter = (
  existingFilterConditions: Filter['filterConditions'],
  newFilterConditions: Filter['filterConditions']
) =>
  Object.keys(existingFilterConditions).length === 1 &&
  Object.keys(existingFilterConditions).length === Object.keys(newFilterConditions).length;

const checkIfBothFiltersCanHaveINComparator = (
  existingFilterConditions: Filter['filterConditions'],
  newFilterConditions: Filter['filterConditions']
) =>
  newFilterConditions.F0.comparator === 'EQ' &&
  (existingFilterConditions.F0.comparator === newFilterConditions.F0.comparator ||
    existingFilterConditions.F0.comparator === 'IN');

const checkIfCombinableWithINComparator = (
  existingFilterConditions: Filter['filterConditions'],
  newFilterConditions: Filter['filterConditions']
) =>
  checkIfBothFilterConditionsHaveASingleFilter(existingFilterConditions, newFilterConditions) &&
  checkIfBothFiltersCanHaveINComparator(existingFilterConditions, newFilterConditions) &&
  existingFilterConditions.F0.ignoreCase === newFilterConditions.F0.ignoreCase;

// flattens the query.select object array to string array to extact columns
const extractSelectQueryColumns = (
  select: {
    field: string;
  }[]
) => select.map((entry) => entry.field);

const combiner = (promise: DataViewCall, newDataViewCall: DataViewCall) => {
  const existingFilterConditions = (promise.query?.filter as Filter).filterConditions;
  const newFilterConditions = (newDataViewCall.query?.filter as Filter).filterConditions;
  let newFilterLogic = (newDataViewCall.query?.filter as Filter).logic;
  const existingFiltersLength = Object.keys(existingFilterConditions).length;

  if (checkIfCombinableWithINComparator(existingFilterConditions, newFilterConditions)) {
    if (existingFilterConditions.F0.comparator === 'EQ') {
      convertFilterToInComparator(existingFilterConditions);
    }

    (existingFilterConditions.F0.rhs.values as string[]).push(newFilterConditions.F0.rhs.value as string);
  } else {
    Object.keys(newFilterConditions).forEach((filter, index) => {
      const newFilterId = `F${existingFiltersLength + index}`;
      existingFilterConditions[newFilterId] = newFilterConditions[filter];
      newFilterLogic = newFilterLogic.replace(filter, newFilterId);
    });
    (promise.query?.filter as Filter).logic += ` OR ${newFilterLogic}`;
  }

  const existingSelect = extractSelectQueryColumns(
    promise.query?.select as {
      field: string;
    }[]
  );
  const newSelect = extractSelectQueryColumns(
    newDataViewCall.query?.select as {
      field: string;
    }[]
  );
  newSelect.forEach((field) => {
    if (!existingSelect.includes(field)) {
      (
        promise.query?.select as {
          field: string;
        }[]
      ).push({ field });
    }
  });
};

const checkIfBothQueriesHaveEqualDataViewParameters = (promise: DataViewCall, newDataViewCall: DataViewCall) =>
  promise.dataViewParameters &&
  newDataViewCall.dataViewParameters &&
  Object.keys(newDataViewCall.dataViewParameters).length === Object.keys(promise.dataViewParameters).length &&
  !Object.keys(promise.dataViewParameters).some((property) => {
    if (typeof newDataViewCall.dataViewParameters?.[property as keyof typeof promise.dataViewParameters] === 'string') {
      return (
        promise.dataViewParameters?.[property as keyof typeof promise.dataViewParameters] !==
        newDataViewCall.dataViewParameters?.[property as keyof typeof promise.dataViewParameters]
      );
    }
    return !Utils.isEqual(
      promise.dataViewParameters?.[property as keyof typeof promise.dataViewParameters],
      newDataViewCall.dataViewParameters?.[property as keyof typeof promise.dataViewParameters]
    );
  });

const checkIfBothQueriesDoNotHaveDataViewParameters = (promise: DataViewCall, newDataViewCall: DataViewCall) =>
  !promise.dataViewParameters && !newDataViewCall.dataViewParameters;

const checkIfFiltersAreOnSameColumn = (filter: Filter, newFilter: Filter) => {
  if (Object.keys(filter.filterConditions).length % Object.keys(newFilter.filterConditions).length === 0) {
    return !Object.keys(newFilter.filterConditions).some(
      (key) => filter.filterConditions[key].lhs.field !== newFilter.filterConditions[key]?.lhs.field
    );
  }
  return false;
};

const comparator = (promise: DataViewCall, newDataViewCall: DataViewCall) =>
  checkIfFiltersAreOnSameColumn(promise.query?.filter as Filter, newDataViewCall.query?.filter as Filter) &&
  (checkIfBothQueriesHaveEqualDataViewParameters(promise, newDataViewCall) ||
    checkIfBothQueriesDoNotHaveDataViewParameters(promise, newDataViewCall));

const separator = (
  combinedDataViewCall: DataViewCall & {
    dataViewCallMap: {
      [identifier: string]: { promise: Promise<unknown>; resolve: (value: unknown) => void };
    };
  },
  responseData: unknown
) => {
  const { data } = responseData as { data: { [key: string]: string }[] };
  const { dataViewCallMap, filterDataOnClient } = combinedDataViewCall;
  Object.keys(dataViewCallMap).forEach((identifier) => {
    dataViewCallMap[identifier].resolve({ data: filterDataOnClient(identifier, data) });
  });
};

const addDefaultBufferFunctions = (bufferedCallParams: {
  combiner?: DataViewCallCombiner;
  comparator?: DataViewCallComparator;
  separator?: DataViewCallSeparator;
  time?: number;
  bufferSize?: number;
}): {
  combiner: DataViewCallCombiner;
  comparator: DataViewCallComparator;
  separator: DataViewCallSeparator;
  time?: number;
  bufferSize?: number;
} => {
  return { combiner, comparator, separator, ...bufferedCallParams };
};

export default addDefaultBufferFunctions;
